import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  signal,
} from '@angular/core';

import {
  resolveSnackbarDurationMs,
  SnackbarData,
} from '../components/snackbar/snackbar.model';
import { SnackbarStackComponent } from '../components/snackbar/snackbar-stack.component';

export interface SnackbarStackItem {
  id: string;
  data: SnackbarData;
}

export interface SnackbarRef {
  dismiss(): void;
}

@Injectable({
  providedIn: 'root',
})
export class SnackbarService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly document = inject(DOCUMENT);
  private readonly environmentInjector = inject(EnvironmentInjector);

  private readonly stackSignal = signal<SnackbarStackItem[]>([]);
  private readonly dismissTimeouts = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private stackRef?: ComponentRef<SnackbarStackComponent>;

  readonly stack = this.stackSignal.asReadonly();

  show(data: SnackbarData): SnackbarRef {
    this.ensureStackMounted();

    const id = this.createId();
    const item: SnackbarStackItem = { id, data };
    this.stackSignal.update((items) => [...items, item]);

    const durationMs = resolveSnackbarDurationMs(data.duration);

    if (durationMs !== null) {
      const timeoutId = setTimeout(() => this.dismiss(id), durationMs);
      this.dismissTimeouts.set(id, timeoutId);
    }

    return {
      dismiss: () => this.dismiss(id),
    };
  }

  dismiss(id: string): void {
    const timeoutId = this.dismissTimeouts.get(id);

    if (timeoutId) {
      clearTimeout(timeoutId);
      this.dismissTimeouts.delete(id);
    }

    this.stackSignal.update((items) => items.filter((item) => item.id !== id));
  }

  private ensureStackMounted(): void {
    if (this.stackRef) {
      return;
    }

    this.stackRef = createComponent(SnackbarStackComponent, {
      environmentInjector: this.environmentInjector,
    });

    this.applicationRef.attachView(this.stackRef.hostView);
    this.document.body.appendChild(this.stackRef.location.nativeElement);
    this.stackRef.changeDetectorRef.detectChanges();
  }

  private createId(): string {
    return `snackbar-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }
}
